import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useMutation, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { ChevronLeft } from "lucide-react";
import { CampaignForm } from "@/components/corporate/campaign/CampaignForm";
import { CampaignFormValues } from "@/components/corporate/campaign/CampaignFormSchema";
import { Location } from "@/components/corporate/campaign/LocationSelector";
import { fetchCampaignById, updateCampaign, fetchOpportunities } from "@/lib/api/campaign";
import { format, parseISO } from "date-fns";

const CorporateEditCampaignsPage = ({ session, profile }: { session: any; profile: any }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State for campaign data
  const [initialGoals, setInitialGoals] = useState<string[]>([""]);
  const [initialLocations, setInitialLocations] = useState<Location[]>([{
    country: "",
    state: "",
    district: "",
    volunteers: 1,
    details: ""
  }]);
  const [initialSelectedOpportunities, setInitialSelectedOpportunities] = useState<string[]>([]);
  
  // Fetch campaign data
  const { data: campaignData, isLoading: isLoadingCampaign } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => fetchCampaignById(session?.access_token, id as string),
    enabled: !!session?.access_token && !!id,
    onSuccess: (data) => {
      // Set initial goals
      if (data.meta?.goals && Array.isArray(data.meta.goals)) {
        setInitialGoals(data.meta.goals.length > 0 ? data.meta.goals : [""]);
      }
      
      // Set initial locations
      if (data.locations && Array.isArray(data.locations)) {
        if (data.locations.length > 0) {
          const formattedLocations = data.locations.map((location: any) => ({
            country: location.country || "",
            state: location.state || "",
            district: location.district || "",
            volunteers: location.meta?.volunteers || 1,
            details: location.meta?.details || ""
          }));
          setInitialLocations(formattedLocations);
        } else {
          // Ensure at least one empty location
          setInitialLocations([{
            country: "",
            state: "",
            district: "",
            volunteers: 1,
            details: ""
          }]);
        }
      }
      
      // Set initial selected opportunities
      if (data.opportunities && Array.isArray(data.opportunities)) {
        const opportunityIds = data.opportunities.map((opp: any) => opp.id);
        setInitialSelectedOpportunities(opportunityIds);
      }
    }
  });
  
  // Format campaign data for the form
  const formattedCampaignData = campaignData ? {
    name: campaignData.name,
    meta: {
      description: campaignData.meta?.description || "",
      theme: campaignData.meta?.theme || "",
      goals: initialGoals,
    },
    active_from: campaignData.active_from ? parseISO(campaignData.active_from) : new Date(),
    active_to: campaignData.active_to ? parseISO(campaignData.active_to) : new Date(),
    is_active: campaignData.is_active !== undefined ? campaignData.is_active : true,
    additional_details: campaignData.additional_details || "",
  } : undefined;
  
  // Fetch opportunities with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingOpportunities
  } = useInfiniteQuery({
    queryKey: ['opportunities'],
    queryFn: ({ pageParam = 1 }) => {
      return fetchOpportunities({
        pageParam,
        token: session?.access_token,
        limit: 6 // Reduced from default 12 to make pagination more visible
      });
    },
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      // Check if there are more pages available
      const nextPage = pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;
      return nextPage;
    },
    enabled: !!session?.access_token
  });
  
  // Log when new data is received
  useEffect(() => {
    if (data) {
    }
  }, [data, hasNextPage]);
  
  // Flatten all opportunities from all pages
  const opportunities = data?.pages.flatMap(page => page.data) || [];
  
  // Update campaign mutation
  const { mutate, isPending } = useMutation({
    mutationFn: (campaignData: any) => updateCampaign(session?.access_token, id as string, campaignData),
    onSuccess: (data) => {
      toast({
        title: "Campaign updated",
        description: "Your campaign has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      navigate(`/dashboard/campaigns/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });
  
  // Handle form submission
  const handleSubmit = (
    data: CampaignFormValues, 
    goals: string[], 
    locations: Location[], 
    selectedOpportunities: string[]
  ) => {
    // Prepare campaign data for submission
    const campaignData = {
      ...data,
      meta: {
        ...data.meta,
        goals
      },
      locations: locations.map(location => ({
        country: location.country,
        state: location.state,
        district: location.district,
        meta: {
          volunteers: location.volunteers,
          details: location.details
        }
      })),
      opportunities: selectedOpportunities.map(id => ({ id }))
    };
    
    // Submit the campaign data
    mutate(campaignData);
  };
  
  if (isLoadingCampaign) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/campaigns')}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Campaigns
            </Button>
            <h1 className="text-2xl font-bold">Edit Campaign</h1>
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Loading campaign data...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/campaigns')}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <h1 className="text-2xl font-bold">Edit Campaign</h1>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Edit Campaign</CardTitle>
          <CardDescription>
            Update your campaign by modifying the information below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CampaignForm
            initialData={formattedCampaignData}
            initialGoals={initialGoals}
            initialLocations={initialLocations}
            initialSelectedOpportunities={initialSelectedOpportunities}
            opportunities={opportunities}
            isLoadingOpportunities={isLoadingOpportunities}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            onSubmit={handleSubmit}
            submitButtonText="Update Campaign"
            isSubmitting={isPending}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CorporateEditCampaignsPage;
